/**
 * Список пациентов врача.
 *
 * Отображает карточки всех привязанных пациентов с:
 * - ИПС и трендом
 * - флагами риска (выделяются цветом)
 * - датой последнего отчёта
 *
 * Пациенты с флагами риска отображаются первыми.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { doctorAPI } from '../../api/client';
import { PatientListItem, Patient, RiskFlag } from '../../types';

/** Метки для флагов риска */
const RISK_LABELS: Record<RiskFlag, string> = {
  high_score: 'Высокий ИПС',
  worsening_trend: 'Ухудшение',
  extreme_symptom: 'Экстрем. симптом',
  missed_medication: 'Пропуск лекарств',
};

const PatientsListPage: React.FC = () => {
  const [patients, setPatients] = useState<PatientListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(false);
  const [search, setSearch] = useState('');
  const [unassigned, setUnassigned] = useState<Patient[]>([]);
  const [assigning, setAssigning] = useState<number | null>(null);
  const [filterMsType, setFilterMsType] = useState('');
  const [filterTrend, setFilterTrend] = useState('');
  const [sortBy, setSortBy] = useState<'risk' | 'score' | 'name' | 'last_report'>('risk');
  const navigate = useNavigate();

  const loadPatients = useCallback(async () => {
    try {
      const res = await doctorAPI.getPatients();
      setPatients(res.data.patients);
    } catch (err) {
      console.error('Ошибка загрузки списка пациентов:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  const searchUnassigned = async () => {
    try {
      const res = await doctorAPI.getUnassignedPatients(search);
      setUnassigned(res.data.patients);
    } catch (err) {
      console.error('Ошибка поиска пациентов:', err);
    }
  };

  useEffect(() => {
    if (showAssign) searchUnassigned();
  }, [showAssign]);

  const handleAssign = async (patientId: number) => {
    setAssigning(patientId);
    try {
      await doctorAPI.assignPatient(patientId);
      setUnassigned(unassigned.filter((p) => p.id !== patientId));
      await loadPatients();
    } catch (err) {
      console.error('Ошибка привязки пациента:', err);
    } finally {
      setAssigning(null);
    }
  };

  // Фильтрация и сортировка
  const filteredPatients = patients
    .filter((p) => !filterMsType || p.ms_type === filterMsType)
    .filter((p) => !filterTrend || p.trend === filterTrend)
    .sort((a, b) => {
      if (sortBy === 'score') return b.integrated_score - a.integrated_score;
      if (sortBy === 'name') return a.last_name.localeCompare(b.last_name);
      if (sortBy === 'last_report') {
        if (!a.last_report_date) return 1;
        if (!b.last_report_date) return -1;
        return a.last_report_date.localeCompare(b.last_report_date);
      }
      // default: risk (больше флагов — первым)
      return b.risk_flags.length - a.risk_flags.length;
    });

  if (loading) return <div style={{ padding: 40 }}>Загрузка...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>Мои пациенты</h1>
        <button
          style={styles.assignToggle}
          onClick={() => setShowAssign(!showAssign)}
        >
          {showAssign ? 'Скрыть' : '+ Добавить пациента'}
        </button>
      </div>

      {/* Панель привязки пациентов */}
      {showAssign && (
        <div style={styles.assignPanel}>
          <h3 style={{ margin: '0 0 12px' }}>Поиск пациентов без врача</h3>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input
              type="text" value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchUnassigned()}
              placeholder="Имя, фамилия или email..."
              style={styles.searchInput}
            />
            <button onClick={searchUnassigned} style={styles.searchBtn}>
              Найти
            </button>
          </div>
          {unassigned.length === 0 ? (
            <p style={{ color: '#6b7280', fontSize: 14 }}>Пациенты не найдены</p>
          ) : (
            unassigned.map((p) => (
              <div key={p.id} style={styles.unassignedRow}>
                <span>{p.last_name} {p.first_name} ({p.email})</span>
                <button
                  style={styles.assignBtn}
                  onClick={() => handleAssign(p.id)}
                  disabled={assigning === p.id}
                >
                  {assigning === p.id ? '...' : 'Привязать'}
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* Фильтры и сортировка */}
      {patients.length > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={filterMsType} onChange={(e) => setFilterMsType(e.target.value)} style={styles.filterSelect}>
            <option value="">Все типы РС</option>
            <option value="RRMS">RRMS</option>
            <option value="PPMS">PPMS</option>
            <option value="SPMS">SPMS</option>
            <option value="PRMS">PRMS</option>
          </select>
          <select value={filterTrend} onChange={(e) => setFilterTrend(e.target.value)} style={styles.filterSelect}>
            <option value="">Все тренды</option>
            <option value="improving">Улучшение</option>
            <option value="stable">Стабильно</option>
            <option value="worsening">Ухудшение</option>
            <option value="critical">Критично</option>
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)} style={styles.filterSelect}>
            <option value="risk">По флагам риска</option>
            <option value="score">По ИПС (выше)</option>
            <option value="name">По имени</option>
            <option value="last_report">По дате отчёта</option>
          </select>
          <span style={{ fontSize: 13, color: '#6b7280' }}>
            Показано: {filteredPatients.length} из {patients.length}
          </span>
        </div>
      )}

      {filteredPatients.length === 0 ? (
        <p style={{ color: '#6b7280' }}>
          {patients.length === 0 ? 'У вас пока нет привязанных пациентов' : 'Нет пациентов по выбранным фильтрам'}
        </p>
      ) : (
        <div style={styles.grid}>
          {filteredPatients.map((patient) => (
            <div
              key={patient.id}
              style={{
                ...styles.card,
                borderLeft: patient.risk_flags.length > 0
                  ? '4px solid #dc2626'
                  : '4px solid #16a34a',
              }}
              onClick={() => navigate(`/doctor/patients/${patient.id}`)}
            >
              {/* Заголовок карточки */}
              <div style={styles.cardHeader}>
                <h3 style={styles.patientName}>
                  {patient.last_name} {patient.first_name}
                </h3>
                <span style={{
                  ...styles.score,
                  color: patient.integrated_score >= 7 ? '#dc2626' :
                         patient.integrated_score >= 4 ? '#f59e0b' : '#16a34a',
                }}>
                  ИПС: {patient.integrated_score.toFixed(1)}
                </span>
              </div>

              {/* Метаданные */}
              <div style={styles.meta}>
                <span>Тренд: {patient.trend_ru}</span>
                {patient.ms_type && <span>Тип РС: {patient.ms_type}</span>}
                {patient.last_report_date && (
                  <span>Последний отчёт: {patient.last_report_date}</span>
                )}
              </div>

              {/* Флаги риска */}
              {patient.risk_flags.length > 0 && (
                <div style={styles.flags}>
                  {patient.risk_flags.map((flag) => (
                    <span key={flag} style={styles.flagBadge}>
                      {RISK_LABELS[flag]}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: 16,
  },
  card: {
    backgroundColor: 'var(--bg-card)',
    padding: 20,
    borderRadius: 12,
    boxShadow: 'var(--shadow-card)',
    cursor: 'pointer',
    transition: 'box-shadow 0.2s',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  patientName: { fontSize: 18, margin: 0 },
  score: { fontSize: 18, fontWeight: 700 },
  meta: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    fontSize: 13,
    color: 'var(--text-secondary)',
    marginBottom: 8,
  },
  flags: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  flagBadge: {
    backgroundColor: 'var(--bg-danger)',
    color: '#dc2626',
    padding: '3px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 500,
  },
  assignToggle: {
    backgroundColor: '#2563eb', color: '#fff', border: 'none',
    padding: '10px 20px', borderRadius: 8, cursor: 'pointer',
    fontSize: 14, fontWeight: 600,
  },
  assignPanel: {
    backgroundColor: 'var(--bg-card)', padding: 20, borderRadius: 12,
    boxShadow: 'var(--shadow-card)', marginBottom: 20,
  },
  searchInput: {
    flex: 1, padding: '8px 14px', border: '1px solid var(--border-color)',
    borderRadius: 8, fontSize: 14,
  },
  searchBtn: {
    backgroundColor: '#2563eb', color: '#fff', border: 'none',
    padding: '8px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 14,
  },
  unassignedRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 0', borderBottom: '1px solid var(--border-light)', fontSize: 14,
  },
  assignBtn: {
    backgroundColor: '#16a34a', color: '#fff', border: 'none',
    padding: '6px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
  },
  filterSelect: {
    padding: '8px 12px', border: '1px solid var(--border-color)', borderRadius: 8,
    fontSize: 14, backgroundColor: 'var(--bg-card)', cursor: 'pointer',
  },
};

export default PatientsListPage;
